import React, {useRef, useEffect, useState, useMemo, useCallback} from "react";
import {useReactToPrint} from "react-to-print";
import {
  ChevronDown,
  Download,
  PlaySquare,
  Clock,
  Timer,
  Calendar,
  CheckCircle,
  Monitor,
  Tag,
  CirclePercent,
  CirclePlay,
  ClockFading,
  CircleDashed,
  ArrowDownWideNarrow,
  Film,
} from "lucide-react";
import {formatTime} from "../../../utils/datetime";
import {getAuthHeader} from "../../../config";
import {downloadCsv} from "../../../utils/csv";
import {toLookupId} from "../../../utils/customerAuth";
import getProgressData from "../../video/utils/getProgressData";
import getWatchedSeconds from "../../video/utils/getWatchedSeconds";
import getFurthestPosition from "../../video/utils/getFurthestPosition";

const OrbitLoader = () => (
  <div className="flex flex-col items-center justify-center space-y-4">
    <div className="relative h-12 w-12">
      <div className="absolute inset-0 border-4 border-indigo-100 rounded-full"></div>
      <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
    </div>
  </div>
);

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  if (isNaN(date)) return "N/A";
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

const options = [
  {value: "date", label: "By date"},
  {value: "name", label: "By video name"},
  {value: "progress", label: "By progress"},
];

const StatCard = ({icon, label, sub, value, color}) => (
  <div className="bg-[#F8FAFC] p-5 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col justify-between h-full min-h-[160px] overflow-hidden">
    <div className="flex justify-between items-start mb-4 gap-3">
      {/* flex-1: Takes up available space
		  leading-tight: Keeps lines close when they wrap 
	  */}
      <span className="font-semibold text-sm text-[#191919] flex-1 leading-tight">
        {label}
      </span>

      {/* flex-shrink-0: Prevents the icon from getting squashed */}
      <div
        className={`flex-shrink-0 flex items-center justify-center ${color} w-10 h-10 rounded-2xl shadow-lg`}
      >
        {React.cloneElement(icon, {size: 20})}
      </div>
    </div>

    <div className="flex flex-col">
      <div className="text-[1.25rem] font-semibold text-[#191919] whitespace-nowrap">
        {value}
      </div>
      <div className="text-[#191919]/50 text-[11px] leading-tight">{sub}</div>
    </div>
  </div>
);

const Account = ({loggedInUser}) => {
  const [user, setUser] = useState(null);
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [activeFilter, setActiveFilter] = useState(1);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const printRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const originalWarn = console.warn;
    console.warn = (...args) => {
      if (args[0]?.includes?.("react-to-print.mjs")) return;
      originalWarn(...args);
    };
    return () => {
      console.warn = originalWarn;
    };
  }, []);

  // `background` refetches (tab refocus / route re-entry) must NOT flip
  // isLoading, or the whole dashboard blanks to the full-screen OrbitLoader
  // every time the user tabs back. Only the initial mount load shows it.
  const fetchAccountData = useCallback(
    async ({background = false} = {}) => {
      if (!loggedInUser?.customer_id) return;
      try {
        if (!background) setIsLoading(true);
        const headers = {
          "Content-Type": "application/json",
          Authorization: await getAuthHeader(),
        };
        const lookupId = toLookupId(loggedInUser.customer_id);

        const avatarPromise = loggedInUser?.avatar
          ? fetch(
              `${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-contact/avatar/${loggedInUser.avatar}`,
              {headers},
            )
          : Promise.resolve(null);
        const [userRes, videoRes, avatarRes] = await Promise.all([
          fetch(
            `${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-contact/id/${lookupId}`,
            {headers},
          ),
          fetch(`${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-videos`, {
            headers,
          }),
          avatarPromise,
        ]);
        const userDataJson = await userRes.json();
        const videoDataJson = await videoRes.json();
        let avatarDataJson = null;
        if (avatarRes) avatarDataJson = await avatarRes.json();
        if (videoDataJson.result === "success") setVideos(videoDataJson.data);
        if (userDataJson.result === "success" && userDataJson.data.length > 0)
          setUser(userDataJson.data[0]);
        if (
          avatarDataJson?.result === "success" &&
          avatarDataJson?.imageURL &&
          userDataJson?.data?.[0]?.avatar
        )
          setUser((prev) => ({
            ...prev,
            avatarUrl: avatarDataJson.imageURL,
          }));
      } catch (err) {
        console.error(err);
      } finally {
        if (!background) setIsLoading(false);
      }
    },
    [loggedInUser],
  );

  useEffect(() => {
    fetchAccountData();
  }, [fetchAccountData]);

  // The server snapshot fetched on mount goes stale as the learner keeps
  // watching (watch-time syncs to the backend every ~10s from useSync). This
  // view never polled, so returning to it showed old numbers. Refetch in the
  // background whenever the tab becomes visible or regains focus -- matching
  // how people navigate back to the dashboard -- so the cards reflect the
  // latest synced totals without a full-screen reload.
  useEffect(() => {
    const refetch = () => fetchAccountData({background: true});
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refetch();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", refetch);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", refetch);
    };
  }, [fetchAccountData]);

  const downloadPDF = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Activity_Report_${loggedInUser?.customer_id || "User"}`,
    pageStyle: `
      @page { margin: 10mm; size: A4; }
      @media print {
        html, body { height: auto !important; overflow: visible !important; }
        body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background-color: white !important; }
        main { overflow: visible !important; height: auto !important; width: 100% !important; padding: 0 !important; margin: 0 !important; display: block !important; }
        .grid-cols-5 { 
            display: grid !important; 
            grid-template-columns: repeat(5, 1fr) !important; 
            gap: 8px !important; 
            width: 100% !important;
        }
        .grid-cols-12 { display: grid !important; grid-template-columns: repeat(12, minmax(0, 1fr)) !important; }
        .group { page-break-inside: avoid !important; break-inside: avoid !important; display: block !important; margin-bottom: 0.5rem !important; }
      }
    `,
  });

  const downloadCSV = () => {
    if (!stats) return;
    const {totals, sorted} = stats;
    const rows = [
      ["Metric", "Value"],
      ["Customer", `${user.customer_firstname} ${user.customer_lastname}`],
      ["ID", user.customer_id],
      ["Date Range", `${startDate || "Start"} to ${endDate || "End"}`],
      ["Platform Time", totals.secondsActive],
      ["Hours Reported (Playtime)", totals.videoSecs],
      ["", ""],
      ["Video ID", "Video Title", "Category", "Time Watched (Seconds)"],
    ];
    sorted.forEach(([id, videoData]) => {
      const info = getVideoInfo(id);
      rows.push([id, info.title, info.category, videoData.time?.toFixed(2)]);
    });
    downloadCsv(rows, `my_analytics_${user.customer_id}.csv`);
  };

  const getVideoInfo = (id) => {
    const video = videos.find((v) => v.id.toString() === id.toString());
    return (
      video || {title: `Video #${id}`, category: "General", videoLength: 0}
    );
  };

  const stats = useMemo(() => {
    // Return zeroed-out defaults instead of null so the StatCards always have
    // numeric values to format. Prevents "undefined%" / "NaNs" when the user
    // record exists but has no activity yet, or when the user fetch hasn't
    // populated `user` yet on first render.
    const totals = {secondsActive: 0, videoSecs: 0};
    const history = {};

    const isWithinRange = (dateStr) => {
      if (!startDate || !endDate) return true;
      return dateStr >= startDate && dateStr <= endDate;
    };

    if (user?.activity) {
      Object.entries(user.activity).forEach(([date, day]) => {
        if (isWithinRange(date)) {
          // Coerce in case DDB returns the attribute as a string.
          totals.secondsActive += Number(day?.secondsActive) || 0;
          totals.videoSecs += Number(day?.totalVideoSeconds) || 0;
        }
      });
    }

    if (user?.watch_history) {
      Object.entries(user.watch_history).forEach(([videoId, data]) => {
        let lastD = null;
        // The activity stat cards above are month-scoped, but the Watch
        // History list is cumulative -- a video finished in an earlier month
        // must still appear (QA: "completed videos not showing in watch
        // history"). Sum the full dailyBreakdown and keep any watched video.
        const totalTime = Object.entries(data?.dailyBreakdown || {}).reduce(
          (sum, [d, v]) => {
            if (!lastD || new Date(d) > new Date(lastD)) lastD = d;
            return sum + (Number(v) || 0);
          },
          0,
        );
        if (totalTime > 0 || getFurthestPosition(data) > 0)
          history[videoId] = {
            time: totalTime,
            lastDate: lastD,
            // Furthest playback position ever reached (monotonic
            // maxPosition, falling back to the resume position for legacy
            // entries). Completion is judged off this (matching the
            // cards/modal and the Admin view), NOT off accumulated
            // watch-seconds which routinely fall a little short of the
            // length, and NOT off the resume position, which regresses on
            // rewatch.
            lastPosition: getFurthestPosition(data),
          };
      });
    }

    const sorted = Object.entries(history).sort((a, b) => {
      const infoA = getVideoInfo(a[0]);
      const infoB = getVideoInfo(b[0]);

      if (sortBy === "name") {
        return infoA.title.localeCompare(infoB.title);
      } else if (sortBy === "date") {
        return new Date(b[1].lastDate) - new Date(a[1].lastDate);
      } else if (sortBy === "progress") {
        const percA = a[1].lastPosition / (infoA.videoLength || 1);
        const percB = b[1].lastPosition / (infoB.videoLength || 1);
        return percB - percA;
      }
      return b[1].time - a[1].time;
    });

    const completionPercentage = Math.min(
      100,
      Math.floor((totals.videoSecs / 288000) * 100),
    );
    return {totals, sorted, completionPercentage};
    // eslint-disable-next-line
  }, [user, sortBy, videos, startDate, endDate]);

  const setMonthRange = (offset) => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() - offset + 1, 0);
    setStartDate(firstDay.toISOString().split("T")[0]);
    setEndDate(lastDay.toISOString().split("T")[0]);
    setActiveFilter(offset);
  };

  // Render the current month and the two preceding months (e.g. on
  // 10 Jun this shows Jun / May / Apr). Khubaib's QA noted the previous
  // [1,2,3] offsets hid the current month entirely -- dashboards in
  // this product always include the in-progress month so users can see
  // their activity as it happens.
  const monthButtons = useMemo(() => {
    return [0, 1, 2].map((offset) => {
      const d = new Date();
      d.setMonth(d.getMonth() - offset);
      return {
        label: d.toLocaleString("default", {month: "long"}),
        offset: offset,
      };
    });
  }, []);

  // Default filter is the current month to match the month-chip order.
  useEffect(() => {
    setMonthRange(0);
  }, []);

  // flex-1 centers the loader in the leftover viewport height (<main> and
  // the view wrappers are flex columns); py-24 keeps sane spacing anywhere
  // this renders outside that flex context.
  if (isLoading)
    return (
      <div
        className="flex-1 py-24 flex flex-col items-center justify-center gap-4"
        role="status"
      >
        <OrbitLoader />
        <p className="text-sm text-slate-500">Loading your activity...</p>
      </div>
    );

  // No min-h-screen: 100vh is taller than the visible mobile viewport
  // (the app shell is 100dvh) and this page flows inside the scrolling
  // <main>, so forced viewport height shoved the footer over the last
  // rows of content on mobile Chrome.
  return (
    <div className="bg-white p-4 md:p-8 font-sans">
      {/* User Info Header */}
      <div className="mb-10 w-full rounded-[2.5rem] p-4 md:p-3 border border-[#ECECEC] flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex flex-row items-center gap-4 w-full md:w-auto">
          <div className="w-10 h-10 md:w-14 md:h-14 rounded-full overflow-hidden border-4 border-white shadow-md">
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                className="w-full h-full object-cover"
                alt="profile"
              />
            ) : (
              <div className="w-full h-full bg-slate-200 flex items-center justify-center font-bold text-slate-400 text-xl">
                {user?.customer_firstname?.charAt(0)}
              </div>
            )}
          </div>
          <div className="md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2">
              <h2 className="text-xl font-semibold text-[#191919] tracking-tight">
                {user?.customer_firstname} {user?.customer_lastname}
              </h2>
              <span className="bg-blue-50 text-blue-500 text-[10px] px-2 py-0.5 rounded-full font-black uppercase tracking-wider">
                You
              </span>
            </div>
            <p className="text-[#191919]/50 text-sm font-medium">
              {user?.customer_id}
            </p>
          </div>
        </div>

        <div className="w-full md:w-auto relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-full md:w-auto bg-[#1a1a1a] text-white px-8 py-3 rounded-full flex items-center justify-between md:justify-center gap-3 font-bold text-sm shadow-xl"
          >
            <div className="flex items-center gap-2">
              <Download size={18} /> Download report
            </div>
            <ChevronDown
              size={18}
              className={showDropdown ? "rotate-180" : ""}
            />
          </button>
          {showDropdown && (
            <>
              <div
                className="fixed inset-0 z-[60]"
                onClick={() => setShowDropdown(false)}
              ></div>

              <div className="absolute right-0 left-0 md:left-auto top-full mt-2 md:w-60 bg-white rounded-[2rem] shadow-2xl border border-slate-100 p-4 z-[70]">
                <div className="text-[10px] font-bold text-slate-400 tracking-widest uppercase mb-4 ml-2">
                  Download Report
                </div>

                <div className="space-y-1">
                  <button
                    className="w-full flex items-center gap-3 p-2 rounded-[1.5rem] bg-blue-50/50 hover:bg-blue-50 transition-colors group text-left"
                    onClick={() => {
                      setShowDropdown(false);
                      downloadPDF();
                    }}
                  >
                    <div className="relative flex-shrink-0">
                      <div className="w-6 h-8 bg-white rounded border border-slate-200 flex flex-col items-center justify-center relative overflow-hidden shadow-sm">
                        <div className="absolute bottom-1 left-0 right-0 bg-[#E11D48] text-[8px] text-white font-bold text-center py-0.5">
                          PDF
                        </div>
                      </div>
                    </div>
                    <span className="text-slate-800 text-sm font-semibold truncate">
                      Download as PDF
                    </span>
                  </button>

                  <button
                    className="w-full flex items-center gap-3 p-2 rounded-[1.5rem] hover:bg-slate-50 transition-colors group text-left"
                    onClick={() => {
                      setShowDropdown(false);
                      downloadCSV();
                    }}
                  >
                    <div className="relative flex-shrink-0">
                      <div className="w-6 h-8 bg-white rounded border border-slate-200 flex flex-col items-center justify-center relative overflow-hidden shadow-sm">
                        <div className="absolute bottom-1 left-0 right-0 bg-[#16A34A] text-[8px] text-white font-bold text-center py-0.5">
                          CSV
                        </div>
                      </div>
                    </div>
                    <span className="text-slate-800 text-sm font-semibold truncate">
                      Download as CSV
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <main ref={printRef}>
        <section className="mb-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <h3 className="text-xl font-semibold text-[#262626] tracking-tight">
                Your Activity
              </h3>
              <p className="text-[#262626]/50 text-sm font-medium">
                Track your progress and stay on top of your required hours
              </p>
            </div>
            <div className="flex items-center gap-1 p-1.5 rounded-2xl border border-slate-100 w-full md:w-auto">
              {monthButtons.map((m) => (
                <button
                  key={m.offset}
                  onClick={() => setMonthRange(m.offset)}
                  className={`flex-1 md:flex-none px-6 py-2 text-xs font-black rounded-full transition-all ${activeFilter === m.offset ? "bg-[#1a1a1a] text-white shadow-md" : "text-slate-500 hover:bg-white"}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 min-[800px]:grid-cols-3 min-[1100px]:grid-cols-4 min-[1280px]:grid-cols-5 gap-4">
            <StatCard
              icon={<PlaySquare className="text-white" />}
              label="Videos Watched"
              sub="Total videos"
              value={stats.sorted.length}
              color="bg-red-400"
            />
            <StatCard
              icon={<CheckCircle className="text-white" />}
              label="Hours Completed"
              sub="% of required hours"
              value={`${stats.completionPercentage}%`}
              color="bg-blue-400"
            />
            <StatCard
              icon={<Clock className="text-white" />}
              label="Hours Left"
              sub="To meet requirement"
              value={formatTime(Math.max(0, 288000 - stats.totals.videoSecs))}
              color="bg-orange-400"
            />
            <StatCard
              icon={<Monitor className="text-white" />}
              label="Platform Time"
              sub="Time spent here"
              value={formatTime(stats.totals.secondsActive)}
              color="bg-emerald-400"
            />
            <StatCard
              icon={<Timer className="text-white" />}
              label="Video Playtime"
              sub="Work hours completed"
              value={formatTime(stats.totals.videoSecs)}
              color="bg-purple-400"
            />
          </div>
        </section>

        <section className="pb-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
            <div>
              <h3 className="text-xl font-semibold text-[#262626] tracking-tight">
                Your Learning Progress
              </h3>
              <p className="text-[#262626]/50 text-sm font-medium">
                Track your progress and completed videos
              </p>
            </div>

            <div
              className="print:hidden w-full lg:max-w-[240px]"
              ref={dropdownRef}
            >
              <p className="text-[#1a1a1a] text-sm mb-1 font-medium ml-1">
                Sort by:
              </p>

              <div className="relative">
                {/* Main Button */}
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`w-full bg-[#f8faff] border px-3 py-2 rounded-full flex items-center gap-3 transition-all duration-200 ${
                    isDropdownOpen
                      ? "border-[#3b82f6] ring-2 ring-blue-50"
                      : "border-[#e0e8ff]"
                  }`}
                >
                  <ArrowDownWideNarrow
                    size={18}
                    strokeWidth={2}
                    className="text-[#1a1a1a]"
                  />
                  <span className="flex-1 text-left text-sm font-medium text-[#1a1a1a]">
                    {options.find((opt) => opt.value === sortBy)?.label}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`text-[#1a1a1a] transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {/* Custom Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute top-[110%] left-0 w-full bg-white border border-slate-100 rounded-[32px] shadow-2xl z-[100] p-4 animate-in fade-in zoom-in duration-150">
                    <p className="text-[#94a3b8] text-xs font-bold tracking-widest uppercase mb-4 ml-4">
                      SORT BY
                    </p>

                    <div className="flex flex-col gap-1">
                      {options.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-[20px] text-sm font-medium transition-colors ${
                            sortBy === option.value
                              ? "bg-[#e5eefb] text-[#1a1a1a]"
                              : "bg-transparent text-[#1a1a1a] hover:bg-slate-50"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4 md:hidden">
            {stats?.sorted.map(([id, data]) => {
              const info = getVideoInfo(id);
              const perc = Math.min(
                100,
                Math.floor((data.lastPosition / (info.videoLength || 1)) * 100),
              );
              const {label: statusLabel, completed: isCompleted} =
                getProgressData(data.lastPosition, info);
              return (
                <div
                  key={id}
                  className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm"
                >
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-14 h-10 rounded-xl bg-slate-200 overflow-hidden shrink-0 shadow-sm">
                      <img
                        src={`https://picsum.photos/seed/${id}/100/100`}
                        className="w-full h-full object-cover"
                        alt="icon"
                      />
                    </div>
                    <h4 className="font-semibold text-[#191919] text-sm leading-snug">
                      {info.title}
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 gap-y-4 text-[11px] font-semibold">
                    <div className="flex items-center gap-2 text-[#191919]/60">
                      <Tag size={14} /> Category
                    </div>
                    <div className="text-[#191919] flex items-center justify-end gap-1">
                      <span className="text-sm">🧩</span>
                      <div className="text-[#191919] font-medium text-right">
                        {info.category}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[#191919]/60">
                      <Calendar size={14} /> Date
                    </div>
                    <div className="text-[#191919] font-medium text-right">
                      {formatDate(data.lastDate)}
                    </div>
                    <div className="flex items-center gap-2 text-[#191919]/60">
                      <CirclePercent size={14} /> Progress
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-32 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-indigo-500"
                          style={{width: `${perc}%`}}
                        />
                      </div>
                      <span className="text-[#191919] font-medium text-right">
                        {perc}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[#191919]/60">
                      <CirclePlay size={14} /> Watched
                    </div>
                    <div className="text-[#191919] font-medium text-right">
                      {formatTime(getWatchedSeconds(data.lastPosition, info))}
                    </div>
                    <div className="flex items-center gap-2 text-[#191919]/60">
                      <ClockFading size={14} /> Length
                    </div>
                    <div className="text-[#191919] font-medium text-right">
                      {formatTime(info.videoLength)}
                    </div>
                    <div className="flex items-center gap-2 text-[#191919]/60">
                      <CircleDashed size={14} /> Status
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] uppercase border font-black ${isCompleted ? "bg-emerald-50 text-emerald-500 border-emerald-100" : "bg-orange-50 text-orange-500 border-orange-100"}`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${isCompleted ? "bg-emerald-500" : "bg-orange-500"}`}
                        />
                        {isCompleted ? statusLabel : "In Progress"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-separate border-spacing-y-2">
              <thead>
                <tr className="text-left text-[10px] font-black text-slate-400 tracking-[0.2em]">
                  <th className="pb-4 px-2">
                    <span className="flex items-center gap-1">
                      <Film size={14} /> Video Name
                    </span>
                  </th>
                  <th className="pb-4 px-2">
                    <span className="flex items-center gap-1">
                      <Tag size={14} /> Category
                    </span>
                  </th>
                  <th className="pb-4 px-2">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} /> Date
                    </span>
                  </th>
                  <th className="pb-4 px-2 text-center">
                    <span className="flex items-center gap-1">
                      <CirclePercent size={14} /> Progress
                    </span>
                  </th>
                  <th className="pb-4 px-2">
                    <span className="flex items-center gap-1">
                      <CirclePlay size={14} /> Watched
                    </span>
                  </th>
                  <th className="pb-4 px-2">
                    <span className="flex items-center gap-1">
                      <ClockFading size={14} /> Length
                    </span>
                  </th>
                  <th className="pb-4 px-2">
                    <span className="flex items-center gap-1">
                      <CircleDashed size={14} /> Status
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {stats?.sorted.map(([id, videoData]) => {
                  const {lastDate, lastPosition} = videoData;
                  const info = getVideoInfo(id);
                  const percentage = Math.min(
                    100,
                    Math.floor((lastPosition / (info.videoLength || 1)) * 100),
                  );
                  const {label: statusLabel, completed: isCompleted} =
                    getProgressData(lastPosition, info);
                  const radius = 16;
                  const circumference = 2 * Math.PI * radius;
                  const offset =
                    circumference - (percentage / 100) * circumference;
                  return (
                    <tr
                      key={id}
                      className="hover:bg-slate-50 transition-colors group"
                    >
                      <td className="py-4 px-2 flex items-center gap-3">
                        <div className="w-10 h-8 rounded-lg bg-slate-200 overflow-hidden shadow-sm">
                          <img
                            src={`https://picsum.photos/seed/${id}/100/100`}
                            className="w-full h-full object-cover"
                            alt="icon"
                          />
                        </div>
                        <span className="font-semibold text-xs text-[#191919]">
                          {info.title}
                        </span>
                      </td>
                      <td className="py-4 px-2 text-xs font-semibold text-[#191919]/80">
                        {info.category}
                      </td>
                      <td className="py-4 px-2 text-[10px] font-semibold text-[#191919]/50">
                        {formatDate(lastDate)}
                      </td>
                      <td className="py-4 px-2 text-center font-black text-xs text-indigo-600">
                        <div className="relative w-10 h-10 flex items-center justify-center">
                          <svg
                            className="w-full h-full transform -rotate-90"
                            viewBox="0 0 40 40"
                          >
                            <circle
                              cx="20"
                              cy="20"
                              r={radius}
                              stroke="#f1f5f9"
                              strokeWidth="2"
                              fill="transparent"
                            />
                            <circle
                              cx="20"
                              cy="20"
                              r={radius}
                              stroke="#3B82F6"
                              strokeWidth="2"
                              fill="transparent"
                              strokeDasharray={circumference}
                              strokeDashoffset={offset}
                              strokeLinecap="round"
                            />
                          </svg>
                          <span className="absolute text-[8px] font-semibold text-[#191919]/50">
                            {percentage}%
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-2 text-[10px] font-semibold text-[#191919]/50">
                        {formatTime(getWatchedSeconds(lastPosition, info))}
                      </td>
                      <td className="py-4 px-2 text-[10px] font-semibold text-[#191919]/50">
                        {formatTime(info.videoLength)}
                      </td>
                      <td className="py-4 px-2">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold ${isCompleted ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-orange-50 text-orange-600 border border-orange-100"}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full shrink-0 ${isCompleted ? "bg-emerald-500" : "bg-orange-500"}`}
                          />
                          {isCompleted ? statusLabel : "In Progress"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Account;
